import { gql } from 'graphql.macro';
import { invoiceConfigurationFragment } from 'apollo/fragments/organization';
import { linkedUserFragment } from './user';
import { customerSettingsFragment, orgAddressFragment } from './common';
import { scannerCaseDetailsFragment, scannerCaseListItemFragment } from './scanner-case';

export const filesUrlFragment = gql`
    fragment filesUrlFields on UploadedFileResponse {
        filename
        mimetype
        url
        _id
        created {
            on
            by {
                _id
                fullName
            }
        }
    }
`;

export const timeFragment = gql`
    fragment timeFields on Time {
        hours
        minutes
        seconds
    }
`;

export const patientFragment = gql`
    fragment patientFields on Patient {
        firstName
        lastName
        personalIdentityNumber
        category
        noPersonalIdentityNumber
        gender
    }
`;

export const associatedActionFragment = gql`
    fragment associatedActionFields on AssociatedAction {
        _id
        isMaterialMandatory
        name
        subCategory
        number
        internalCode
        price
        tiotandvardPrice
        vat
        quotable
        costPerTooth
        actionMaterials {
            _id
            priceDiff
            material
            materialName
        }
        materialMacros {
            _id
            name
            materials {
                _id
                material {
                    _id
                    materialCode
                    description
                    vat
                    batchInventoryManagement
                    batches {
                        batchNumber
                        quantity
                        fullBatchQuantity
                        createdOn
                        closed
                        closedDate
                    }
                }
            }
        }
    }
`;

export const categorizedActionsFragment = gql`
    fragment categorizedActionsFields on CategorizedActions {
        categoryId
        categoryName
        subCategoryId
        subCategoryName
        currency
        actions {
            ...associatedActionFields
        }
    }
    ${associatedActionFragment}
`;

export const internalOrderDetailsFragment = gql`
    fragment internalOrderDetailsFields on InternalOrderDetails {
        _id
        lab
        order
        notes
        externalNotes
        signedUrls {
            filename
            mimetype
            url
            _id
            created {
                on
                by {
                    _id
                    fullName
                }
            }
        }
        constructionsFilesUrls {
            filename
            mimetype
            url
            _id
            created {
                on
                by {
                    _id
                    fullName
                }
            }
        }
        lastUpdated {
            on
            by
        }
    }
`;

export const orderLinkedDentistFragment = gql`
    fragment orderLinkedDentistFields on Order {
        _id
        supplier {
            _id
        }
        customer {
            _id
        }
        linkedDentists {
            user {
                ...linkedUserFields
            }
            dateAdded
        }
    }
    ${linkedUserFragment}
`;

export const orderLinkedExtTechFragment = gql`
    fragment orderLinkedExtTechFields on Order {
        _id
        supplier {
            _id
        }
        customer {
            _id
        }
        linkedExtTechs {
            user {
                ...linkedUserFields
            }
            dateAdded
        }
    }
    ${linkedUserFragment}
`;

export const orderWorkFlowFragment = gql`
    fragment orderWorkFlowFields on Order {
        _id
        linkedExtTechs {
            user {
                _id
            }
            dateAdded
        }
        workFlow {
            _id
            timeInCurrentStep {
                ...timeFields
            }
            currentStep {
                _id
                stepId
                name
                description
                comment
                maxHourInStep
                userId
                dateTime
                workStarted
                isPaused
                pauseReason
                user {
                    fullName
                }
                externalTechnicians {
                    _id
                    fullName
                }
            }
            stepLogs {
                _id
                stepId
                name
                comment
                dateTime
                isPaused
                pauseReason
                maxHourInStep
                user {
                    _id
                    fullName
                }
                externalTechnicians {
                    _id
                    fullName
                }
                timeSpent {
                    ...timeFields
                }
            }
        }
    }
    ${timeFragment}
`;

export const deliveryDetailsFragment = gql`
    fragment deliveryDetailsFields on OrderDeliveryDetails {
        price
        date
        sampleType
        isPickUpAtClinic
        isLabHandleRequest
        isLabHaveOwnResource
        reason
        sample {
            type
            content {
                name
                value
            }
            otherContent
        }
        pickUpLocation {
            _id
            name
            ptjCustomerNumber
            organizationNumber
            contact {
                address {
                    ...orgAddressFields
                }
                postalAddress {
                    ...orgAddressFields
                }
                communication {
                    email
                    phone
                    site
                }
            }
        }
        destination {
            _id
            name
            ptjCustomerNumber
            organizationNumber
            contact {
                address {
                    ...orgAddressFields
                }
                postalAddress {
                    ...orgAddressFields
                }
                communication {
                    email
                    phone
                    site
                }
            }
            clinicInternalNotes {
                lab
                note
            }
            customerSettings {
                ...customerSettingsFields
            }
        }
        internalDeliveryDate
        externalDeliveryDate
        time {
            minute
            hour
        }
    }
    ${orgAddressFragment}
    ${customerSettingsFragment}
`;

export const restorationFragment = gql`
    fragment restorationFields on Restoration {
        _id
        type
        warranty
        isTioTandvard
        legacyConstructionId
        teeth {
            type
            selectedTeeth
            selectedRange {
                start
                end
            }
            selectedJaw
            toothStates {
                tooth
                state
            }
        }
        actions {
            _id
            actionId
            code
            name
            restorationType
            price {
                quantity
                unitPrice
                vatPercent
                totalWithoutVat
                totalWithVat
                originalUnitPrice
                originalTotalWithVat
            }
            warranty
            isTioTandvard
            category
            categoryName
            # Frozen on the row (DD-2506); the order view never reads the catalogue.
            quotable
            costPerTooth
            internalCode
        }
        materials {
            _id
            materialId
            relatedActionId
            code
            name
            batchNumber
            isMaterialSurcharge
            warranty
            isTioTandvard
            price {
                quantity
                unitPrice
                vatPercent
                totalWithoutVat
                totalWithVat
                originalUnitPrice
                originalTotalWithVat
            }
        }
        properties {
            key
            valueType
            value
            text
            flag
            teeth
            units {
                actionRowId
                label
                value
            }
        }
    }
`;

export const orderDetailsFragment = gql`
    fragment orderDetailsFields on Order {
        _id
        orderId
        currency
        panNumber
        panNumberDescription
        additionalInfo
        historicalComments {
            additionalInfo
            createdAt
            updatedBy
            version
        }
        status
        type
        patient {
            ...patientFields
        }
        supplier {
            _id
            name
            contact {
                address {
                    street
                    postalCode
                    city
                    country
                }
                communication {
                    phone
                    email
                }
            }
            invoicing {
                configuration {
                    ...invoiceConfigurationFields
                }
            }
            openingHours {
                day
                time {
                    from
                    to
                }
                active
            }
            holidayDays
            unavailableLabDates
        }
        customer {
            _id
            name
            type
            ptjCustomerNumber
            contact {
                communication {
                    phone
                }
            }
            customerSettings {
                ...customerSettingsFields
            }
            invoicing {
                communication {
                    email
                }
            }
            actions {
                name
                number
            }
            active
        }
        isInternalOrderData
        isOrderDeviations
        isReadyForDelivery
        isDeliveryCreated
        dentist {
            _id
            name {
                firstName
                lastName
            }
            fullName
            phone
            local
            dentistNotes {
                clinic
                lab
                note
            }
        }
        created {
            on
            by {
                _id
                name {
                    firstName
                    lastName
                }
            }
        }
        originalCreationDate
        workFlow {
            _id
            timeInCurrentStep {
                ...timeFields
            }
            currentStep {
                _id
                stepId
                name
                description
                maxHourInStep
                userId
                dateTime
                workStarted
                isPaused
                pauseReason
                externalTechnicians {
                    _id
                    fullName
                }
                user {
                    _id
                    fullName
                }
            }
        }
        linkedExtTechs {
            user {
                _id
            }
            dateAdded
        }
        linkedDentists {
            user {
                _id
                fullName
                email
            }
            dateAdded
        }
        linkedClinics {
            clinic {
                _id
                name
                contact {
                    communication {
                        email
                    }
                }
            }
            dateAdded
        }
        customParticipantEmails
        deliveryDetails {
            ...deliveryDetailsFields
        }
        linkedOrders {
            order
            orderId
            type
        }
        orderRef {
            order
            orderId
            type
        }
        subOrders {
            _id
            orderId
        }
        subOrderWarnings {
            kind
            subOrder {
                _id
                orderId
                supplier {
                    _id
                    name
                }
            }
        }
        subOrderLines {
            subOrder {
                _id
                orderId
                currency
                supplier {
                    _id
                    name
                }
            }
            kind
            restorationId
            restorationType
            teeth {
                type
                selectedTeeth
                selectedRange {
                    start
                    end
                }
                selectedJaw
            }
            code
            name
            batchNumber
            price {
                quantity
                unitPrice
                vatPercent
                totalWithoutVat
                totalWithVat
            }
        }
        # DD-3484/DD-3485: resolve only for the lab that created the sub-order.
        parentOrder {
            _id
            orderId
        }
        subcontractSelection {
            restorationCount
            attachmentCount
            scanCaseFileCount
            patientReference
            patientIdentityNumber
            dentistName
            comment
            confirmedAt
        }
        labFlag
        isProductionFinished
        invoicingState
        invoiceActionTime
        tempInvoiceNumber
        invoiceCreatedTime
        isWarrantyOrder
        rejectStatus
        deliveryNotePdfUrl
        deliveryNoteCreatedDate
        archived {
            by
            byCustomer
            byLab
        }
        invoice {
            _id
            pdfUrl
            status
            invoiceNumber
            sentDate
        }
        isCreatedByLab
        isCopyOrder
        scannerCase {
            ...scannerCaseDetailsFields
        }
        currentPickup {
            _id
            clinic {
                _id
            }
            manualPackageCount
            actualPackageCount
            comment
            orderIds
            transportDate
            serviceCode
            status
            externalId
            internalMessage
        }
        currentDelivery {
            ... on TransportOrder {
                _id
                clinic {
                    _id
                }
                manualPackageCount
                actualPackageCount
                comment
                orderIds
                transportDate
                serviceCode
                status
                externalId
                internalMessage
                isSameDayDelivery
                deadline
            }
            ... on NShiftShipmentWaybill {
                shipDate
                orderNo
                shipmentStatus: status
                prints {
                    id
                    fileUrl
                    type
                }
            }
        }
        messages {
            type
            message
            messageOn
            manualSelectedDate
        }
        decline {
            by
            message
            declineOn
        }
        cancel {
            by
            message
            on
        }
        totalAmount {
            totalPrice
            vatAmount
        }
        rating {
            satisfied
            notSatisfied
            comments
            reasons {
                name
                value
            }
            by {
                _id
                fullName
            }
        }
        isRated
        isCertified
        certified {
            on
            by {
                _id
                fullName
            }
        }
        transportTrackingLink
        version
        filesUrl {
            ...filesUrlFields
        }
        originalOrder {
            _id
            patient {
                ...patientFields
            }
            deliveryDetails {
                ...deliveryDetailsFields
            }
            dentist {
                _id
                fullName
            }
            restorations {
                ...restorationFields
            }
            filesUrl {
                ...filesUrlFields
            }
        }
        availableVersions {
            version
            createdAt
        }
        restorations {
            ...restorationFields
        }
        restorationTypes
        approvedBy {
            fullName
            id
        }
        internalOrderId
        orderTags {
            _id
            name
            color
        }
        isImportedOrder
        orderForms {
            _id
            title
            createdBy {
                name {
                    firstName
                    lastName
                }
                fullName
            }
            createdAt
        }
        clinicOrderVersion
        totalOriginalPrice
        totalOriginalVat
        attachedScannerCaseFiles {
            ...scannerCaseListItemFields
        }
        isClientEnabled
        iteration
        productionPlan {
            stepId
            stepName
            technicianId
            technicianName
            startDate
            deadline
            estimatedProductionHours
            estimatedMachineHours
            stepOrder
            allowOvernight
            startNextDay
            affectedByQuantityModifier
            workUnits
            departmentId
            machineId
        }
    }
    ${patientFragment}
    ${customerSettingsFragment}
    ${timeFragment}
    ${orgAddressFragment}
    ${invoiceConfigurationFragment}
    ${deliveryDetailsFragment}
    ${restorationFragment}
    ${filesUrlFragment}
    ${scannerCaseDetailsFragment}
    ${scannerCaseListItemFragment}
`;

export const orderListFragment = gql`
    fragment orderListFields on Order {
        _id
        orderId
        currency
        isInternalOrderData
        type
        status
        unreadMessagesCount
        shouldShowTruckButton
        panNumber
        panNumberDescription
        manualSelectedSentDate
        supplier {
            _id
            name
        }
        customer {
            _id
            name
            type
        }
        dentist {
            _id
            fullName
        }
        patient {
            firstName
            lastName
            personalIdentityNumber
        }
        deliveryDetails {
            status
            deadlineStatus
            date
            sampleType
            isPickUpAtClinic
            isLabHandleRequest
            isLabHaveOwnResource
            pickUpLocation {
                _id
                name
            }
            destination {
                _id
                name
            }
            internalDeliveryDate
            externalDeliveryDate
            price
        }
        restorationTypes
        constructionMaterials
        isModify
        labFlag
        created {
            on
            by {
                _id
                fullName
            }
        }
        linkedDentists {
            user {
                _id
            }
            dateAdded
        }
        linkedExtTechs {
            user {
                _id
            }
            dateAdded
        }
        isProductionFinished
        internalOrderId
        invoicingState
        invoice {
            _id
            status
        }
        orderTags {
            _id
            name
            color
        }
        workFlow {
            _id
            timeInCurrentStep {
                ...timeFields
            }
            currentStep {
                _id
                stepId
                name
                dateTime
                isPaused
                pauseReason
                maxHourInStep
                workStarted
                user {
                    _id
                    fullName
                }
            }
        }
        hasOrderFiles
        archived {
            by
            byCustomer
            byLab
        }
        isImportedOrder
        hasDeliveryNotePdf
    }
    ${timeFragment}
`;

export const orderRowContextFragment = gql`
    fragment orderRowContextFields on Order {
        currency
        _id
        orderId
        internalOrderId
        type
        status
        invoicingState
        isProductionFinished
        isImportedOrder
        isInternalOrderData
        isModify
        labFlag
        restorationTypes
        constructionMaterials
        unreadMessagesCount
        shouldShowTruckButton
        supplier {
            _id
            name
        }
        customer {
            _id
            name
            type
        }
        dentist {
            _id
            fullName
        }
        patient {
            firstName
            lastName
            personalIdentityNumber
        }
        deliveryDetails {
            status
            deadlineStatus
            date
            sampleType
            isPickUpAtClinic
            isLabHandleRequest
            isLabHaveOwnResource
            pickUpLocation {
                _id
                name
            }
            destination {
                _id
                name
            }
            internalDeliveryDate
            externalDeliveryDate
            price
        }
        created {
            on
            by {
                _id
                fullName
            }
        }
        linkedDentists {
            user {
                _id
            }
            dateAdded
        }
        linkedExtTechs {
            user {
                _id
            }
            dateAdded
        }
        orderTags {
            _id
            name
            color
        }
        workFlow {
            _id
            timeInCurrentStep {
                hours
                minutes
                seconds
            }
            currentStep {
                _id
                stepId
                name
                dateTime
                isPaused
                pauseReason
                maxHourInStep
                userId
                workStarted
                user {
                    _id
                    fullName
                }
            }
        }
        archived {
            by
            byCustomer
            byLab
        }
        hasOrderFiles
        hasDeliveryNotePdf
    }
`;

export const stepOrderListFragment = gql`
    fragment stepOrderListFields on Order {
        _id
        orderId
        type
        status
        unreadMessagesCount
        panNumber
        panNumberDescription
        supplier {
            _id
            name
        }
        customer {
            _id
            name
        }
        dentist {
            _id
            fullName
        }
        patient {
            firstName
            lastName
            personalIdentityNumber
        }
        deliveryDetails {
            status
            deadlineStatus
            date
            sampleType
            isPickUpAtClinic
            isLabHandleRequest
            isLabHaveOwnResource
            pickUpLocation {
                _id
                name
            }
            destination {
                _id
                name
            }
            internalDeliveryDate
            externalDeliveryDate
        }
        restorationTypes
        isModify
        labFlag
        created {
            on
            by {
                _id
                fullName
            }
        }
        linkedDentists {
            user {
                _id
            }
            dateAdded
        }
        linkedExtTechs {
            user {
                _id
            }
            dateAdded
        }
        internalOrderId
        orderTags {
            _id
            name
            color
        }
        constructionMaterials
        workFlow {
            _id
            timeInCurrentStep {
                ...timeFields
            }
            currentStep {
                _id
                stepId
                name
                description
                maxHourInStep
                userId
                dateTime
                workStarted
                isPaused
                pauseReason
                user {
                    fullName
                }
            }
        }
        hasOrderFiles
        hasDeliveryNotePdf
        productionPlan {
            stepId
            stepName
        }
    }

    ${timeFragment}
`;

export const deliveryOrdersFragment = gql`
    fragment deliveryOrdersFields on Order {
        _id
        orderId
        status
        customer {
            _id
            name
        }
        dentist {
            _id
            fullName
        }
        patient {
            firstName
            lastName
        }
        deliveryDetails {
            date
            pickUpLocation {
                _id
                name
            }
            destination {
                _id
                name
            }
            internalDeliveryDate
            externalDeliveryDate
            sampleType
        }
        internalOrderId
        manualSelectedReceivedDate
        restorationTypes
        workFlow {
            _id
            currentStep {
                stepId
                name
                departmentName
                isPaused
                pauseReason
                workStarted
                user {
                    _id
                    fullName
                }
            }
        }
    }
`;
